import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";
import { addQueryParams, reverse } from "utils/urls";

import Box from "components/Box";
import DealInfo from "components/DealPageLayout/SubNavigation/DealInfo";
import NoPrint from "components/NoPrint";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";

import BouncingDotsLoader from "pages/Deal/BouncingDotsLoader";
import { EChecklistSubmissionDialogStep } from "pages/Deal/DueDiligence/constants";

import { useDealDetails } from "entities/Deal/sdk";
import {
  companyPackageSnapshotCreate,
  useCompanyPackageSnapshotList,
  usePackageDealDetails,
} from "entities/Package/sdk";

import PrintableCategory from "./components/PrintableCategory";

const PrintableChecklist = () => {
  const hasRun = useRef(false);
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();
  const [searchParams] = useSearchParams();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const submissionStep =
    (searchParams.get("submissionStep") as EChecklistSubmissionDialogStep) ||
    undefined;

  const snapshotName = searchParams.get("snapshotName") || undefined;
  const currentDueDiligenceTab = searchParams.get("tab") || "";

  const cacheKey = useMemo(() => new Date().toString(), []);

  const { data: checklist } = usePackageDealDetails({
    packageId,
    dealId,
    queryParams: { cacheKey },
  });
  const { data: deal } = useDealDetails({ dealId });

  const { mutate: mutateSnapshots } = useCompanyPackageSnapshotList({
    dealId,
    companyPackageId: packageId,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (_.isNil(checklist) || hasRun.current) {
      return;
    }

    hasRun.current = true;

    companyPackageSnapshotCreate({
      dealId,
      companyPackageId: packageId,
      name: snapshotName,
      html: "<!DOCTYPE html>" + document.documentElement.outerHTML,
    })
      .then((snapshot) => {
        toast.successMessage(
          "The snapshot has been successfully created. Currently we are generating a PDF and a ZIP file. When it is done we will attach them to the snapshot!"
        );
        mutateSnapshots();
        navigate(
          addQueryParams(
            reverse(URLS.DEAL_PACKAGE_NO_TAB, {
              dealId,
              packageId,
            }),
            {
              ...(!_.isNil(submissionStep) && { submissionStep }),
              snapshotId: snapshot.id,
              tab: currentDueDiligenceTab,
            }
          )
        );
      })
      .catch((error) => {
        handleInvalidRequest(error);
        navigate(
          addQueryParams(
            reverse(URLS.DEAL_PACKAGE_NO_TAB, {
              dealId,
              packageId,
            }),
            {
              tab: currentDueDiligenceTab,
              submissionStep: EChecklistSubmissionDialogStep.CLOSED,
            }
          )
        );
      });
  }, [
    dealId,
    navigate,
    packageId,
    checklist,
    hasRun,
    snapshotName,
    mutateSnapshots,
    currentDueDiligenceTab,
    submissionStep,
  ]);

  const currentDate = useMemo(
    () => format(new Date(), FORMATS.FRONTEND.DATETIME),
    []
  );

  return (
    <Stack
      sx={{
        color: colors.black,
        gap: 1.5,
        "@page": {
          marginTop: "40px",
          marginLeft: 0,
          marginRight: 0,
          marginBottom: "90px",
          size: "letter portrait",
        },
        "@page:first": {
          marginTop: 0,
        },
      }}
    >
      <NoPrint>
        <BouncingDotsLoader loadingText="Snapshot is being generated..." />
      </NoPrint>
      <Stack
        width="100%"
        height="85px"
        sx={{
          backgroundColor: colors.gray10,
          paddingX: 2,
        }}
        alignItems="flex-start"
        justifyContent="center"
      >
        <DealInfo deal={deal} />
      </Stack>
      <Stack sx={{ marginLeft: 5, marginRight: 5 }}>
        <Text variant="h2">{checklist?.name}</Text>
        <Text variant="text3" color={colors.gray60}>
          {checklist?.company?.name}
        </Text>
        <Stack
          sx={{
            backgroundColor: colors.gray20,
            borderRadius: 2,
            padding: theme.spacing(1.5, 1.2),
            marginTop: 1.2,
          }}
          width="fit-content"
        >
          <Text variant="text3" fontWeight={700}>
            Snapshotted version saved on {currentDate}
          </Text>
        </Stack>

        {checklist?.tabs.map((tab) => (
          <Stack key={tab.tab_id} marginTop={2}>
            <Stack
              sx={{
                marginBottom: 2,
              }}
            >
              <Stack direction="row" gap={2} justifyContent="space-between">
                <Stack gap={0.5}>
                  <Text variant="h3">{tab.name}</Text>
                </Stack>
              </Stack>
            </Stack>
            {_.uniqBy(tab?.categories, "category_id").map((category) => (
              <Stack>
                <PrintableCategory
                  key={category.category_id}
                  category={category}
                />
              </Stack>
            ))}
            {_.isEmpty(tab?.categories) && (
              <Box sx={{ paddingY: 5 }}>
                <Text variant="h5" textAlign="center">
                  No results.
                </Text>
              </Box>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default PrintableChecklist;
